import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { useLegalOs, type LegalOsBundle, type LegalOsKeyword, type LegalOsPage } from "../hooks/useLegalOs";
import { WizardSteps } from "../components/WizardSteps";

type Filter = "fixed" | "hold";

const FILTER_LABEL: Record<Filter, string> = {
  fixed: "今すぐ作るページ",
  hold: "あとで検討",
};

function compactKeyword(keyword?: string | null): string {
  return (keyword ?? "").replace(/\s+/g, " ").trim();
}

function supportKeywordsFor(keyword: LegalOsKeyword): string[] {
  const text = compactKeyword(keyword.keyword);
  if (!text) return [];
  const tokens = text.split(" ").filter(Boolean);
  const area = tokens.find((token) => /市|区|町|村|県|全域|全国/.test(token)) ?? "";
  const service = tokens.find((token) => !/市|区|町|村|県|全域|全国|行政書士|相談|代行|費用|料金|無料相談/.test(token)) ?? "";
  const candidates = [
    area && service ? `${area} ${service} 料金` : "",
    area && service ? `${area} ${service} 相談` : "",
    service ? `${service} 必要書類` : "",
    service ? `${service} 自分でできる` : "",
  ].filter(Boolean);
  return Array.from(new Set(candidates)).filter((item) => item !== text).slice(0, 4);
}

export function KeywordStrategyPage() {
  const { caseRow, os, setOs, loading } = useLegalOs();
  const [filter, setFilter] = useState<Filter>("fixed");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const autoGenerated = useRef(false);

  const keywords = os?.keywords ?? [];
  const pages = os?.pages ?? [];
  const pageById = useMemo(() => new Map<string, LegalOsPage>(pages.map((page) => [page.id, page])), [pages]);
  const normalizeStatus = (status: string | null | undefined): Filter => {
    if (status === "hold" || status === "deferred") return "hold";
    return "fixed";
  };
  const counts = useMemo(() => ({
    fixed: keywords.filter((k) => normalizeStatus(k.fixed_status) === "fixed").length,
    hold: keywords.filter((k) => normalizeStatus(k.fixed_status) === "hold").length,
  }), [keywords]);
  const visible = keywords.filter((k) => normalizeStatus(k.fixed_status) === filter);
  const keywordGroups = useMemo(() => {
    const byPage = new Map<string, LegalOsKeyword[]>();
    const noPage: LegalOsKeyword[] = [];
    for (const item of visible) {
      if (!item.target_page_id) {
        noPage.push(item);
        continue;
      }
      const list = byPage.get(item.target_page_id) ?? [];
      list.push(item);
      byPage.set(item.target_page_id, list);
    }
    const groups = Array.from(byPage.entries()).map(([pageId, items]) => {
      const sorted = [...items].sort((a, b) => b.priority_score - a.priority_score);
      const main = sorted[0]!;
      return {
        key: pageId,
        main,
        parentPage: pageById.get(pageId) ?? null,
        subKeywords: sorted.slice(1).map((item) => item.keyword),
        generatedSubKeywords: sorted.length === 1 ? supportKeywordsFor(main) : [],
      };
    });
    return [
      ...groups,
      ...noPage.map((main) => ({ key: main.id, main, parentPage: null, subKeywords: [] as string[], generatedSubKeywords: supportKeywordsFor(main) })),
    ];
  }, [visible, pageById]);

  const updateKeyword = async (item: LegalOsKeyword, fixed_status: Filter) => {
    if (!caseRow) return;
    setBusy(item.id);
    setError(null);
    try {
      const res = await api.patch<{ os: LegalOsBundle }>(`/api/cases/${caseRow.id}/keywords/${item.id}`, { fixed_status });
      setOs(res.os);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "キーワード更新に失敗しました");
    } finally {
      setBusy(null);
    }
  };

  const regenerate = async () => {
    if (!caseRow) return;
    setBusy("regen");
    setError(null);
    try {
      const res = await api.post<{ os: LegalOsBundle }>(`/api/cases/${caseRow.id}/strategy/generate`, {});
      setOs(res.os);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "AI再分析に失敗しました");
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (!loading && caseRow && keywords.length === 0 && !autoGenerated.current) {
      autoGenerated.current = true;
      void regenerate();
    }
  }, [loading, caseRow?.id, keywords.length]);

  if (loading) return <div className="page-center">読み込み中...</div>;

  return (
    <section className="lp-page">
      {error && <div className="alert alert-error">{error}</div>}
      <WizardSteps current="keywords" />
      <div className="lp-page-head">
        <div>
          <h1>キーワード戦略</h1>
          <p>ヒアリング内容から、AIが検索者ペルソナと検索行動をもとに候補を作りました。</p>
        </div>
        <div className="lp-head-actions">
          <button className="btn btn-ghost" onClick={() => void regenerate()} disabled={busy === "regen"}>
            {busy === "regen" ? "AI再分析中..." : "AIで再分析"}
          </button>
        </div>
      </div>

      {keywords.length === 0 ? (
        <div className="lp-info-banner">
          <strong>まだキーワードがありません。</strong>
          <p>ヒアリング画面で情報を保存すると、AIが候補を生成します。</p>
          <Link className="btn btn-primary btn-sm" to="/hearing">ヒアリングへ戻る</Link>
        </div>
      ) : (
        <div className="lp-keyword-layout">
          <div className="lp-card">
            <div className="lp-tabs">
              {(Object.keys(FILTER_LABEL) as Filter[]).map((key) => (
                <button key={key} className={filter === key ? "active" : ""} onClick={() => setFilter(key)}>
                  {FILTER_LABEL[key]} <span>{counts[key]}</span>
                </button>
              ))}
            </div>
            <div className="lp-keyword-list">
              {keywordGroups.map((group, index) => {
                const item = group.main;
                const subKeywords = [...group.subKeywords, ...group.generatedSubKeywords];
                return (
                <div key={group.key} className="lp-keyword-row">
                  <span className="lp-number">{index + 1}</span>
                  <div>
                    <h2>{item.keyword}</h2>
                    <p>{item.reason ?? "検索意図と事務所の強みに合わせて選定しました。"}</p>
                    <small>{item.primary_intent} / 優先度 {item.priority_score}</small>
                    {subKeywords.length > 0 && (
                      <div className="lp-subkeyword-tree">
                        <strong>このページを支えるサブキーワード</strong>
                        <ul>
                          {subKeywords.map((keyword) => <li key={keyword}>{keyword}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                  <span className={`lp-priority ${index === 0 && filter === "fixed" ? "top" : ""}`}>{index === 0 && filter === "fixed" ? "最優先" : FILTER_LABEL[filter]}</span>
                  <div className="lp-row-page">
                    <small>紐づく親ページ</small>
                    {group.parentPage ? (
                      <Link to="/page-plan">
                        {group.parentPage.page_type === "home" || group.parentPage.url_path === "/" ? "トップページ" : group.parentPage.title}
                      </Link>
                    ) : (
                      <span>未設定</span>
                    )}
                    <em>{item.recommended_page_type ?? "専門ページ"}</em>
                  </div>
                  <div className="lp-inline-actions">
                    {filter !== "fixed" && <button className="btn btn-ghost btn-sm" disabled={busy === item.id} onClick={() => void updateKeyword(item, "fixed")}>採用</button>}
                    {filter !== "hold" && <button className="btn btn-ghost btn-sm" disabled={busy === item.id} onClick={() => void updateKeyword(item, "hold")}>保留</button>}
                  </div>
                </div>
              );})}
              {keywordGroups.length === 0 && <p className="muted">この分類のキーワードはありません。</p>}
            </div>
            <div className="lp-actions-row">
              <Link to="/assets" className="btn btn-primary btn-lg">素材を確認してページ設計へ進む →</Link>
            </div>
          </div>

          <aside className="lp-side-panel">
            <div className="lp-card">
              <h2>AIの選定根拠</h2>
              <ul className="lp-icon-list">
                <li><span>人</span>相談者ペルソナの検索行動から逆算</li>
                <li><span>地</span>地域名と業務名を組み合わせて依頼意図を強める</li>
                <li><span>問</span>料金・不安・代行など問い合わせ直前の言葉を重視</li>
              </ul>
            </div>
            <div className="lp-card">
              <h2>次の画面でできること</h2>
              <p>採用したキーワードに合わせて、ページ生成に使う写真・ロゴ・料金表を確認できます。</p>
              <Link to="/assets">素材管理へ進む →</Link>
            </div>
          </aside>
        </div>
      )}

    </section>
  );
}
